import { NextPage } from "next";
import { useEffect, useRef, useState } from "react";
import { DashboardApi, DashboardData } from "domain/User/Common/utils/dashboard-api";
import { ApiConfig } from "utils/backend-api/api-config";
import { INextPageProps } from "app";
import { useTranslations } from "next-intl";
import Seo, { RobotsMeta } from "domain/SeoAndAnalytics/Seo";
import AlertService from "components/Layout/Alerts/utils/alert-service";
import { useStores } from "hooks";
import { languageConfig } from "i18n/language-config";
import { loadServerTranslations } from "../../../i18n/messages";
import AccountDashboard from "domain/User/Account/Dashboard/AccountDashboard";
import { withAuthAndCustomerTypeProtection } from "domain/User/Common/withAuthAndCustomerTypeProtection";
import { CustomerType } from "stores/user.store";

interface IProps extends INextPageProps {}

const defaultDashboardData: DashboardData = {
  reports: [],
  activeReportsCount: 0,
  expiredReportsCount: 0,
  remainingCreditsCount: 0,
  lastActiveUserPackage: null,
  totalPages: 0,
};

const Account: NextPage<IProps> = () => {
  const dashboardApi = new DashboardApi(ApiConfig.buildBackendUrlForClient());
  const tDashboard = useTranslations("page-account-dashboard");
  const tCommon = useTranslations("common");

  const { userStore } = useStores();

  const isRequestOngoing = useRef(true);
  const [dashboardData, setDashboardData] = useState<DashboardData>(defaultDashboardData);
  const [isLoading, setIsLoading] = useState(true);

  const getDashboardData = async () => {
    try {
      setIsLoading(true);
      const data = await dashboardApi.getDashboardOverview();
      data.reports.sort((a, b) => new Date(b.expiryDate).getTime() - new Date(a.expiryDate).getTime());
      if (isRequestOngoing.current === true) {
        setDashboardData(data);
        userStore.setLastActiveUserPackages(data.lastActiveUserPackage);
      }
    } catch (error) {
      AlertService.showAlert(tCommon("errors.tryAgain"));
    } finally {
      if (isRequestOngoing.current === true) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    getDashboardData();
    return () => {
      isRequestOngoing.current = false;
    };
  }, []);

  return (
    <>
      <Seo title={tDashboard("seo.title")} description={tDashboard("seo.description")} />
      <AccountDashboard dashboardData={dashboardData} isLoading={isLoading} />
    </>
  );
};

export const getServerSideProps = async ({ params: { lang = null } = {} }) => {
  if (!languageConfig.isLanguageAllowedInPath(lang)) {
    return {
      notFound: true,
    };
  }

  const locale = languageConfig.mapLanguageToLocale(lang);

  return {
    props: {
      locale,
      messages: await loadServerTranslations(locale, ["common", "page-account-dashboard", "page-account-reports"]),
    },
  };
};

export default withAuthAndCustomerTypeProtection(Account, {
  allowedCustomerType: CustomerType.B2C,
  redirectRouteName: "businessAccount",
  robotsMeta: RobotsMeta.NO_INDEX_FOLLOW,
});
